// src/redux/services/cameraApi.js
// 📹 CAMERA API - RTK Query (Normalized baseUrl + Bearer + x-auth-token)

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const normalizeBaseUrl = () => {
  const raw = process.env.REACT_APP_BACKEND_URL || "http://localhost:7600";
  const trimmed = raw.replace(/\/$/, "");
  return trimmed.endsWith("/api") ? trimmed : `${trimmed}/api`;
};

const baseQuery = fetchBaseQuery({
  baseUrl: normalizeBaseUrl(),
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem("token");

    if (token) {
      headers.set("x-auth-token", token);
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

export const cameraApi = createApi({
  reducerPath: "cameraApi",
  baseQuery,
  tagTypes: ["Cameras", "CameraStats", "CameraTypes"],
  endpoints: (builder) => ({
    // ===== GET CAMERA TYPES =====
    // NOTE: Returns ARRAY directly (already unwrapped)
    getCameraTypes: builder.query({
      query: () => "/cameras/types",
      providesTags: ["CameraTypes"],
      transformResponse: (response) => response?.data || response || [],
    }),

    // ===== GET ALL CAMERAS (Admin) =====
    getAllCameras: builder.query({
      query: (params) => ({
        url: "/cameras",
        params: {
          page: params?.page || 1,
          limit: params?.limit || 20,
          status: params?.status,
          cameraType: params?.cameraType,
          deviceId: params?.deviceId,
        },
      }),
      providesTags: ["Cameras"],
      transformResponse: (response) => ({
        cameras: response?.data || [],
        total: response?.total || 0,
        page: response?.page || 1,
        pages: response?.pages || 1,
      }),
    }),

    // ===== GET CAMERAS BY DEVICE =====
    // NOTE: Returns ARRAY directly (already unwrapped)
    getCamerasByDevice: builder.query({
      query: (deviceId) => `/cameras/device/${deviceId}`,
      providesTags: (result, error, deviceId) => [{ type: "Cameras", id: deviceId }],
      transformResponse: (response) => response?.data || response || [],
    }),

    // ===== GET SINGLE CAMERA =====
    // NOTE: Returns OBJECT directly (already unwrapped)
    getCamera: builder.query({
      query: (cameraId) => `/cameras/${cameraId}`,
      providesTags: (result, error, cameraId) => [{ type: "Cameras", id: cameraId }],
      transformResponse: (response) => response?.data || response || null,
    }),

    // ===== GET CAMERA STATS FOR DEVICE =====
    // NOTE: Returns OBJECT directly (already unwrapped)
    getCameraStats: builder.query({
      query: (deviceId) => `/cameras/device/${deviceId}/stats`,
      providesTags: (result, error, deviceId) => [{ type: "CameraStats", id: deviceId }],
      transformResponse: (response) => response?.data || response || {},
    }),

    // ===== ADD NEW CAMERA =====
    addCamera: builder.mutation({
      query: (cameraData) => ({
        url: "/cameras",
        method: "POST",
        body: cameraData,
      }),
      invalidatesTags: ["Cameras", "CameraTypes"],
    }),

    // ===== UPDATE CAMERA =====
    updateCamera: builder.mutation({
      query: ({ cameraId, ...cameraData }) => ({
        url: `/cameras/${cameraId}`,
        method: "PUT",
        body: cameraData,
      }),
      invalidatesTags: (result, error, { cameraId }) => [
        "Cameras",
        { type: "Cameras", id: cameraId },
        "CameraTypes",
      ],
    }),

    // ===== DELETE CAMERA =====
    deleteCamera: builder.mutation({
      query: (cameraId) => ({
        url: `/cameras/${cameraId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Cameras", "CameraTypes"],
    }),

    // ===== TOGGLE CAMERA STATUS =====
    toggleCameraStatus: builder.mutation({
      query: (cameraId) => ({
        url: `/cameras/${cameraId}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, cameraId) => [
        "Cameras",
        { type: "Cameras", id: cameraId },
      ],
    }),
  }),
});

export const {
  useGetCameraTypesQuery,
  useGetAllCamerasQuery,
  useGetCamerasByDeviceQuery,
  useLazyGetCamerasByDeviceQuery,
  useGetCameraQuery,
  useGetCameraStatsQuery,
  useAddCameraMutation,
  useUpdateCameraMutation,
  useDeleteCameraMutation,
  useToggleCameraStatusMutation,
} = cameraApi;
